// @ts-nocheck
import { useState } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Group,
  Text,
  Button,
  Modal,
  TextInput,
  Textarea,
  Select,
  TagsInput,
  Badge,
  ActionIcon,
  Tooltip,
  Checkbox,
  Loader,
  Center,
} from '@mantine/core';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { Dropzone, MIME_TYPES } from '@mantine/dropzone';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { useForm } from '@mantine/form';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import {
  IconUpload,
  IconFile,
  IconDownload,
  IconEdit,
  IconTrash,
  IconSearch,
  IconFileDescription,
  IconX,
  IconHistory,
} from '@tabler/icons-react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import {
  useDocuments,
  useUploadDocument,
  useUpdateDocument,
  useDeleteDocument,
} from '../../hooks/useDocuments';
import { useEmployee } from '../../hooks/useEmployees';
import type { DocumentCategory } from '../../types/document';

// ── Constants ───────────────────────────────────────────────────────

const DOCUMENT_CATEGORIES: { value: DocumentCategory; label: string }[] = [
  { value: 'EMPLOYEE_FILE', label: 'Employee File' },
  { value: 'TAX_FORM', label: 'Tax Form' },
  { value: 'POLICY_DOCUMENT', label: 'Policy Document' },
  { value: 'HANDBOOK', label: 'Handbook' },
  { value: 'CONTRACT', label: 'Contract' },
  { value: 'CERTIFICATION', label: 'Certification' },
  { value: 'TRAINING', label: 'Training' },
  { value: 'PERFORMANCE_REVIEW', label: 'Performance Review' },
  { value: 'DISCIPLINARY', label: 'Disciplinary' },
  { value: 'BENEFITS', label: 'Benefits' },
  { value: 'ONBOARDING', label: 'Onboarding' },
  { value: 'OFFBOARDING', label: 'Offboarding' },
  { value: 'COMPLIANCE', label: 'Compliance' },
  { value: 'OTHER', label: 'Other' },
];

const CATEGORY_COLORS: Record<string, string> = {
  EMPLOYEE_FILE: 'blue',
  TAX_FORM: 'red',
  POLICY_DOCUMENT: 'violet',
  HANDBOOK: 'indigo',
  CONTRACT: 'orange',
  CERTIFICATION: 'teal',
  TRAINING: 'cyan',
  PERFORMANCE_REVIEW: 'yellow',
  DISCIPLINARY: 'pink',
  BENEFITS: 'green',
  ONBOARDING: 'lime',
  OFFBOARDING: 'gray',
  COMPLIANCE: 'grape',
  OTHER: 'dark',
};

const ACCEPTED_MIME_TYPES = [
  MIME_TYPES.pdf,
  MIME_TYPES.doc,
  MIME_TYPES.docx,
  MIME_TYPES.xls,
  MIME_TYPES.xlsx,
  MIME_TYPES.csv,
  MIME_TYPES.png,
  MIME_TYPES.jpeg,
  MIME_TYPES.gif,
  'text/plain',
  'application/rtf',
];

// ── Helpers ─────────────────────────────────────────────────────────

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / Math.pow(1024, i);
  return `${size.toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
}

function categoryLabel(category: string): string {
  const found = DOCUMENT_CATEGORIES.find((c) => c.value === category);
  return found?.label ?? category;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1] || result;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ── Row type ────────────────────────────────────────────────────────

interface DocumentRow extends Record<string, unknown> {
  id: string;
  originalId?: string;
  version?: number;
  isActive?: boolean;
  name: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  category: DocumentCategory;
  description?: string;
  employeeId?: string;
  employeeName?: string;
  tags: string[];
  storageKey: string;
  downloadUrl?: string;
  requiresAcknowledgment: boolean;
  acknowledgedCount?: number;
  totalRecipients?: number;
  uploadedBy: string;
  uploadedAt: string;
  createdAt: string;
}

// ── Component ───────────────────────────────────────────────────────

export default function EmployeeDocumentsPage() {
  const { id: employeeId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DocumentRow | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const { data: employeeData, loading: empLoading } = useEmployee(employeeId || '');
  const employee = employeeData?.employee;

  const { data: queryData, loading } = useDocuments({
    page,
    perPage: 25,
    category: categoryFilter || undefined,
    employeeId: employeeId,
    search: searchQuery || undefined,
  });
  const data = queryData;

  const uploadDoc = useUploadDocument();
  const deleteDoc = useDeleteDocument();

  // Cast employee to readable shape
  const emp = employee as { id?: string; firstName?: string; lastName?: string } | undefined;
  const employeeName = emp
    ? `${emp.firstName || ''} ${emp.lastName || ''}`.trim()
    : 'Employee';

  // ── Upload form ─────────────────────────────────────────────────

  const uploadForm = useForm({
    initialValues: {
      name: '',
      description: '',
      category: 'EMPLOYEE_FILE' as string,
      tags: [] as string[],
      requiresAcknowledgment: false,
    },
    validate: {
      name: (v: string) => (v && v.trim() ? null : 'Document name is required'),
      category: (v: string) => (v ? null : 'Category is required'),
    },
  });

  const handleFileSelect = (files: File[]) => {
    if (files.length > 0) {
      const file = files[0] ?? null;
      setSelectedFile(file);
      if (file && !uploadForm.values.name) {
        uploadForm.setFieldValue('name', file.name.replace(/\.[^/.]+$/, ''));
      }
    }
  };

  const handleUploadSubmit = uploadForm.onSubmit(async (values) => {
    if (!selectedFile || !employeeId) {
      notifications.show({
        title: 'Error',
        message: !selectedFile ? 'Please select a file.' : 'Employee ID is missing.',
        color: 'red',
      });
      return;
    }

    try {
      const fileBase64 = await fileToBase64(selectedFile);

      await uploadDoc.mutateAsync({
        name: values.name,
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        mimeType: selectedFile.type || 'application/octet-stream',
        category: values.category,
        description: values.description || undefined,
        employeeId,
        tags: values.tags.length > 0 ? values.tags : undefined,
        requiresAcknowledgment: values.requiresAcknowledgment,
        fileBase64,
      });
      notifications.show({
        title: 'Document Uploaded',
        message: `"${values.name}" uploaded for ${employeeName}.`,
        color: 'green',
      });
      setUploadOpen(false);
      uploadForm.reset();
      setSelectedFile(null);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to upload document';
      notifications.show({
        title: 'Upload Failed',
        message,
        color: 'red',
      });
    }
  });

  // ── Delete handler ──────────────────────────────────────────────

  const handleDelete = async () => {
    if (!deleteTarget || !deleteReason.trim()) return;
    try {
      await deleteDoc.mutateAsync({ id: deleteTarget.id, reason: deleteReason });
      notifications.show({
        title: 'Document Deleted',
        message: 'Document soft-deleted (append-only).',
        color: 'green',
      });
      setDeleteTarget(null);
      setDeleteReason('');
    } catch (err) {
      notifications.show({
        title: 'Delete Failed',
        message: err instanceof Error ? err.message : 'Failed to delete document',
        color: 'red',
      });
    }
  };

  // ── Table columns ───────────────────────────────────────────────

  const columns: Column<DocumentRow>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (row) => (
        <Group gap="xs" wrap="nowrap">
          <IconFileDescription size={16} color="var(--mantine-color-blue-6)" />
          <div>
            <Text size="sm" fw={500} lineClamp={1}>{row.name}</Text>
            <Text size="xs" c="dimmed" lineClamp={1}>{row.fileName}</Text>
          </div>
        </Group>
      ),
    },
    {
      key: 'category',
      label: 'Category',
      width: 150,
      sortable: true,
      render: (row) => (
        <Badge variant="light" color={CATEGORY_COLORS[row.category] || 'gray'} size="sm">
          {categoryLabel(row.category)}
        </Badge>
      ),
    },
    {
      key: 'fileSize',
      label: 'Size',
      width: 90,
      sortable: true,
      render: (row) => <Text size="sm" c="dimmed">{formatFileSize(row.fileSize)}</Text>,
    },
    {
      key: 'tags',
      label: 'Tags',
      width: 160,
      render: (row) =>
        row.tags && row.tags.length > 0 ? (
          <Group gap={4} wrap="wrap">
            {row.tags.slice(0, 2).map((tag) => (
              <Badge key={tag} variant="outline" size="xs" color="gray">{tag}</Badge>
            ))}
            {row.tags.length > 2 && (
              <Text size="xs" c="dimmed">+{row.tags.length - 2}</Text>
            )}
          </Group>
        ) : (
          <Text size="xs" c="dimmed">--</Text>
        ),
    },
    {
      key: 'uploadedAt',
      label: 'Date',
      width: 110,
      sortable: true,
      render: (row) => (
        <Text size="sm" c="dimmed">{new Date(row.uploadedAt).toLocaleDateString()}</Text>
      ),
    },
    {
      key: 'version',
      label: 'Ver.',
      width: 60,
      render: (row) => (
        <Group gap={2}>
          <IconHistory size={12} color="var(--mantine-color-dimmed)" />
          <Text size="xs" c="dimmed">v{row.version ?? 1}</Text>
        </Group>
      ),
    },
  ];

  // ── Loading state ───────────────────────────────────────────────

  if (empLoading) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  // ── Render ──────────────────────────────────────────────────────

  return (
    <Stack gap="md">
      <PageHeader
        title={`Documents - ${employeeName}`}
        subtitle={data ? `${data.length} documents` : 'Employee document management'}
        breadcrumbs={[
          { label: 'Employees', to: '/admin/workforce/employees' },
          { label: employeeName, to: `/employees/${employeeId}` },
          { label: 'Documents' },
        ]}
        actions={
          <Button
            icon={<IconUpload size={16} />}
            onClick={() => setUploadOpen(true)}
          >
            Upload Document
          </Button>
        }
      />

      {/* Filters */}
      <Group gap="sm">
        <TextInput
          placeholder="Search documents..."
          icon={<IconSearch size={16} />}
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.currentTarget.value);
            setPage(1);
          }}
          w={280}
        />
        <Select
          placeholder="All categories"
          clearable
          value={categoryFilter}
          onChange={(value) => {
            setCategoryFilter(value);
            setPage(1);
          }}
          data={DOCUMENT_CATEGORIES}
          w={200}
        />
      </Group>

      {/* Document table */}
      <DataTable<DocumentRow>
        columns={columns}
        data={(data as DocumentRow[]) ?? []}
        loading={loading}
        pagination={
          data
            ? {
                page,
                pageSize: 25,
                total: data.length,
                onPageChange: setPage,
              }
            : undefined
        }
        getRowKey={(row) => row.id}
        onRowClick={(row) => navigate(`/documents/${row.id}`)}
        emptyMessage={`No documents for ${employeeName}. Upload a document to get started.`}
        actions={(row) => {
          const isActive = row.isActive !== false;
          return (
            <Group gap={4}>
              {row.downloadUrl && (
                <Tooltip label="Download">
                  <ActionIcon
                    variant="light"
                    size="sm"
                    color="blue"
                    component="a"
                    href={row.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  >
                    <IconDownload size={14} />
                  </ActionIcon>
                </Tooltip>
              )}
              <Tooltip label="View details">
                <ActionIcon
                  variant="light"
                  size="sm"
                  color="gray"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/documents/${row.id}`);
                  }}
                >
                  <IconFileDescription size={14} />
                </ActionIcon>
              </Tooltip>
              {isActive && (
                <Tooltip label="Delete (soft-delete)">
                  <ActionIcon
                    variant="light"
                    size="sm"
                    color="red"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(row);
                    }}
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>
          );
        }}
      />

      {/* ── Upload Modal ──────────────────────────────────────────── */}
      <Modal
        opened={uploadOpen}
        onClose={() => {
          setUploadOpen(false);
          uploadForm.reset();
          setSelectedFile(null);
        }}
        title={`Upload Document for ${employeeName}`}
        size="lg"
      >
        <form onSubmit={handleUploadSubmit}>
          <Stack gap="md">
            <Dropzone
              onDrop={handleFileSelect}
              accept={ACCEPTED_MIME_TYPES}
              maxSize={50 * 1024 * 1024}
              multiple={false}
            >
              <Group
                justify="center"
                gap="xl"
                mih={120}
                style={{ pointerEvents: 'none' }}
              >
                <Dropzone.Accept>
                  <IconUpload size={40} stroke={1.5} color="var(--mantine-color-blue-6)" />
                </Dropzone.Accept>
                <Dropzone.Reject>
                  <IconX size={40} stroke={1.5} color="var(--mantine-color-red-6)" />
                </Dropzone.Reject>
                <Dropzone.Idle>
                  <IconFile size={40} stroke={1.5} color="var(--mantine-color-dimmed)" />
                </Dropzone.Idle>
                <div>
                  <Text size="sm" fw={500}>
                    {selectedFile
                      ? `Selected: ${selectedFile.name} (${formatFileSize(selectedFile.size)})`
                      : 'Drag a file here or click to browse'}
                  </Text>
                  <Text size="xs" c="dimmed" mt={4}>
                    PDF, Word, Excel, CSV, images up to 50 MB
                  </Text>
                </div>
              </Group>
            </Dropzone>

            <TextInput
              label="Document Name"
              placeholder="e.g. W-4 Form 2025"
              required
              {...uploadForm.getInputProps('name')}
            />

            <Select
              label="Category"
              required
              data={DOCUMENT_CATEGORIES}
              {...uploadForm.getInputProps('category')}
            />

            <Textarea
              label="Description"
              placeholder="Brief description of this document..."
              minRows={2}
              {...uploadForm.getInputProps('description')}
            />

            <TagsInput
              label="Tags"
              placeholder="Add tags..."
              data={uploadForm.values.tags}
              {...uploadForm.getInputProps('tags')}
            />

            <Checkbox
              label="Requires employee acknowledgment"
              description="Track when this employee has reviewed the document"
              {...uploadForm.getInputProps('requiresAcknowledgment', { type: 'checkbox' })}
            />

            <Group justify="flex-end">
              <Button
                variant="default"
                onClick={() => {
                  setUploadOpen(false);
                  uploadForm.reset();
                  setSelectedFile(null);
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                icon={<IconUpload size={16} />}
                loading={uploadDoc.isPending}
                disabled={!selectedFile}
              >
                Upload Document
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* ── Delete Confirmation Modal ─────────────────────────────── */}
      <Modal
        opened={deleteTarget !== null}
        onClose={() => {
          setDeleteTarget(null);
          setDeleteReason('');
        }}
        title="Delete Document"
        size="sm"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            This will soft-delete{' '}
            <Text span fw={600}>{deleteTarget?.name}</Text>{' '}
            by creating a new inactive version (append-only). The original file is preserved for audit.
          </Text>
          <Textarea
            label="Reason for deletion"
            placeholder="Required for audit trail..."
            required
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.currentTarget.value)}
            minRows={2}
          />
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setDeleteTarget(null);
                setDeleteReason('');
              }}
            >
              Keep Document
            </Button>
            <Button
              color="red"
              disabled={!deleteReason.trim()}
              loading={deleteDoc.isPending}
              onClick={handleDelete}
            >
              Delete Document
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
